import React, { useState } from 'react';
import { Mail, MessageCircle, MapPin, Phone } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', service: '', message: '' });
  const { toast } = useToast();

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Please fill required fields', description: 'Name, email and message are required.' });
      return;
    }
    toast({ title: 'Message sent!', description: 'Our team will get back to you shortly.' });
    setForm({ name: '', email: '', service: '', message: '' });
  };

  return (
    <section id="contact" className="relative py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="section-label mb-6">Get in touch</div>
        <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-4">
          Send us <span className="text-amber-400">a message</span>
        </h2>
        <p className="text-slate-400 max-w-xl mb-14">
          Tell us about your goals and challenges. Our team will assess your needs and recommend the right AI strategy.
        </p>

        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8">
          <div className="card-dark rounded-3xl p-8">
            <h3 className="text-white text-lg font-semibold mb-6">Contact Info</h3>
            <div className="space-y-5">
              <a href="mailto:info@prathaminnovations.com" className="flex items-start gap-3 text-slate-300 hover:text-amber-300">
                <div className="h-10 w-10 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                  <Mail size={16} className="text-amber-400" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Email</div>
                  <div className="text-sm">info@prathaminnovations.com</div>
                </div>
              </a>
              <a href="https://wa.me/971547473070" target="_blank" rel="noreferrer" className="flex items-start gap-3 text-slate-300 hover:text-amber-300">
                <div className="h-10 w-10 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                  <MessageCircle size={16} className="text-amber-400" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">UAE · WhatsApp</div>
                  <div className="text-sm">+971 54 747 3070</div>
                </div>
              </a>
              <div className="flex items-start gap-3 text-slate-300">
                <div className="h-10 w-10 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                  <Phone size={16} className="text-amber-400" />
                </div>
                <div className="flex-1">
                  <div className="text-xs text-slate-500">India · Call / WhatsApp</div>
                  <div className="text-sm">+91 81267 09857</div>
                  <div className="flex gap-3 mt-1.5">
                    <a href="tel:+918126709857" className="text-xs text-amber-300 hover:text-amber-200 inline-flex items-center gap-1">
                      <Phone size={11} /> Call
                    </a>
                    <span className="text-slate-600 text-xs">·</span>
                    <a href="https://wa.me/918126709857" target="_blank" rel="noreferrer" className="text-xs text-amber-300 hover:text-amber-200 inline-flex items-center gap-1">
                      <MessageCircle size={11} /> WhatsApp
                    </a>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 text-slate-300">
                <div className="h-10 w-10 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                  <MapPin size={16} className="text-amber-400" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Location</div>
                  <div className="text-sm">CWS-1V-224639, 26th Floor, Amber Gem Tower, Ajman, UAE</div>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="card-dark rounded-3xl p-8 space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs text-slate-400">Full Name</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="John Doe"
                  className="mt-2 bg-white/[0.03] border-white/10 text-white placeholder:text-slate-600 h-11"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400">Email Address</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@company.com"
                  className="mt-2 bg-white/[0.03] border-white/10 text-white placeholder:text-slate-600 h-11"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400">What services are you interested in?</label>
              <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                <SelectTrigger className="mt-2 bg-white/[0.03] border-white/10 text-white h-11">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="bg-[#0b1220] border-white/10 text-white">
                  <SelectItem value="managed-services">Managed Services</SelectItem>
                  <SelectItem value="cloud-modernization">Cloud Modernization</SelectItem>
                  <SelectItem value="ai-consultation">AI Consultation</SelectItem>
                  <SelectItem value="cloud-consultant">Cloud Consultant</SelectItem>
                  <SelectItem value="staff-augmentation">Staff Augmentation</SelectItem>
                  <SelectItem value="ai-products">AI Products</SelectItem>
                  <SelectItem value="other">Something else</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400">Message</label>
              <Textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell us more about your project..."
                rows={5}
                className="mt-2 bg-white/[0.03] border-white/10 text-white placeholder:text-slate-600"
              />
            </div>
            <Button type="submit" className="btn-gradient rounded-full h-11 px-8 text-white border-0">
              Submit
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
